import { api } from "./api";

// Consent-aware analytics. First-party events go to our own API only.
// Marketing pixels (GA4/Meta/TikTok) load ONLY when IDs are configured via
// env vars AND the user has granted consent — never by default, never with PII.
const LS = { first: "bf_utm_first", last: "bf_utm_last", anon: "bf_anon", consent: "bf_consent", partner: "bf_partner" };
// Global Privacy Control: honor the browser opt-out for attribution storage/tracking
try { window.__bpGpc = navigator.globalPrivacyControl === true; } catch { window.__bpGpc = false; }
const UTM = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const fired = new Set(); // rerender-safe dedupe within the session
const recentRecurring = new Map(); // key -> ts — rate-limit recurring events (Group B3 error storms)

export function captureUtm(search = window.location.search) {
  try {
    const q = new URLSearchParams(search);
    const found = {};
    let any = false;
    for (const k of UTM) {
      const v = q.get(k);
      if (v) { found[k.replace("utm_", "")] = v.slice(0, 80); any = true; }
    }
    const fb = q.get("fbclid"); // Meta click id — saved on the account record
    if (fb) { found.fbclid = fb.slice(0, 200); any = true; }
    const via = q.get("via"); // sales-partner link — 60-day FIRST-touch attribution
    if (via && !window.__bpGpc) {
      try {
        if (!localStorage.getItem(LS.partner)) { // first touch wins — never overwrite
          localStorage.setItem(LS.partner, JSON.stringify({ code: via.slice(0, 30).toUpperCase(), ts: Date.now() }));
        }
        api.post("/partners/track", { code: via.slice(0, 30).toUpperCase() }).catch(() => {}); // public pages only
      } catch { /* ignore */ }
    }
    if (any) {
      localStorage.setItem(LS.last, JSON.stringify(found));
      if (!localStorage.getItem(LS.first)) localStorage.setItem(LS.first, JSON.stringify(found));
    }
  } catch { /* ignore */ }
}

export function getPartnerCode() {
  try {
    if (window.__bpGpc) return ""; // GPC opt-out: no attribution
    const p = JSON.parse(localStorage.getItem(LS.partner) || "null");
    if (p && p.code && Date.now() - (p.ts || 0) < 60 * 86400000) return p.code; // 60-day window
  } catch { /* ignore */ }
  return "";
}

export function getAttribution() {  try {
    return {
      first_touch: JSON.parse(localStorage.getItem(LS.first) || "{}"),
      last_touch: JSON.parse(localStorage.getItem(LS.last) || "{}"),
    };
  } catch { return { first_touch: {}, last_touch: {} }; }
}

function anonId() {
  try {
    let a = localStorage.getItem(LS.anon);
    if (!a) { a = crypto.randomUUID(); localStorage.setItem(LS.anon, a); }
    return a;
  } catch { return ""; }
}

export function getConsent() {
  try { return localStorage.getItem(LS.consent) || "pending"; } catch { return "pending"; }
}
export function setConsent(v) {
  try { localStorage.setItem(LS.consent, v); } catch { /* ignore */ }
  if (v === "granted") initMarketing();
}

export function trackEvent(name, { detail = "", once = true } = {}) {
  const key = `${name}|${window.location.pathname}|${detail}`;
  if (once && fired.has(key)) return;
  if (!once) {
    const last = recentRecurring.get(key) || 0;
    if (Date.now() - last < 60000) return; // max 1/min for an identical recurring event (error storms)
    recentRecurring.set(key, Date.now());
  }
  fired.add(key);
  const { last_touch } = getAttribution();
  try {
    let lang = "en";
    try { lang = (localStorage.getItem("buildflow.lang") || "en").slice(0, 4); } catch { /* ignore */ }
    api.post("/analytics/event", {
      name,
      path: window.location.pathname,
      anon: anonId(),
      lang,
      touch: Object.keys(last_touch).length ? "last" : "",
      detail: String(detail).slice(0, 170),
      utm: last_touch,
    }).catch(() => {});
  } catch { /* analytics never breaks the app */ }
  if (getConsent() === "granted") {
    try {
      if (window.gtag) window.gtag("event", name, { page_path: window.location.pathname });
      if (window.fbq) window.fbq("trackCustom", name);
      if (window.ttq) window.ttq.track(name);
    } catch { /* ignore */ }
  }
}

// Global Privacy Control: when the browser sends GPC, no advertising tags load
// and no ad-measurement events fire for that browser (Privacy Policy section 7).
export function gpcOn() {
  try { return navigator.globalPrivacyControl === true; } catch { return false; }
}

// ---- Meta Pixel: standard events with event_id dedup against server CAPI ----
// The pixel loads whenever REACT_APP_META_PIXEL_ID is configured (user-directed
// ad measurement, disclosed in the Privacy Policy). No ID configured → no-op.
export function metaEventId() {
  try { return crypto.randomUUID(); } catch { return `${Date.now()}-${Math.random().toString(36).slice(2)}`; }
}

export function metaTrack(name, eventId) {
  try {
    if (gpcOn()) return;
    if (window.fbq) {
      if (eventId) window.fbq("track", name, {}, { eventID: eventId });
      else window.fbq("track", name);
    }
  } catch { /* ignore */ }
}

function readCookie(n) {
  try { return document.cookie.split("; ").find((x) => x.startsWith(`${n}=`))?.split("=").slice(1).join("=") || ""; } catch { return ""; }
}

// Browser → server mirror so CAPI sends the same event_id for pre-auth events
// (Lead / ViewContent). CompleteRegistration & StartTrial fire CAPI directly
// from the register / first-project endpoints with the browser-supplied id.
export function metaMirror(name, eventId) {
  try {
    if (gpcOn()) return;
    api.post("/analytics/meta", {
      event: name, event_id: eventId, source_url: window.location.href.slice(0, 300),
      attribution: { ...getAttribution().last_touch, fbp: readCookie("_fbp"), fbc: readCookie("_fbc") },
    }).catch(() => {});
  } catch { /* analytics never breaks the app */ }
}

// Landing CTAs carry attribution params through to the signup URL so ads keep
// their utm_*/fbclid chain intact.
export function signupHref() {
  try {
    const { last_touch } = getAttribution();
    const q = new URLSearchParams();
    for (const k of ["source", "medium", "campaign", "content"]) if (last_touch[k]) q.set(`utm_${k}`, last_touch[k]);
    if (last_touch.fbclid) q.set("fbclid", last_touch.fbclid);
    const s = q.toString();
    return s ? `/signup?${s}` : "/signup";
  } catch { return "/signup"; }
}

// Loads ad-platform scripts. Meta loads whenever its ID is configured
// (explicit product decision — see Privacy Policy "Advertising measurement");
// GA4/TikTok stay consent-gated as before. No IDs in env → full no-op.
export function initMarketing() {
  if (gpcOn()) return;
  const meta = process.env.REACT_APP_META_PIXEL_ID;
  try {
    if (meta && !window.fbq) {
      const n = (window.fbq = window.fbq || function fbq() { n.queue.push(arguments); });
      n.queue = n.queue || [];
      n.loaded = true; n.version = "2.0";
      const s = document.createElement("script");
      s.async = true;
      s.src = "https://connect.facebook.net/en_US/fbevents.js";
      document.head.appendChild(s);
      window.fbq("init", meta);
      window.fbq("track", "PageView");
    }
  } catch { /* ignore */ }
  if (getConsent() !== "granted") return;
  const ga = process.env.REACT_APP_GA4_ID;
  const tt = process.env.REACT_APP_TIKTOK_PIXEL_ID;
  try {
    if (ga && !window.gtag) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga)}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", ga, { anonymize_ip: true });
    }
    if (tt && !window.ttq) {
      const t = (window.ttq = window.ttq || []);
      t.methods = ["track", "page"];
      const s = document.createElement("script");
      s.async = true;
      s.src = "https://analytics.tiktok.com/i18n/pixel/events.js";
      document.head.appendChild(s);
      // ttq instance init happens once the SDK loads; guarded queue use only
    }
  } catch { /* ignore */ }
}

// Persists UTM attribution onto the account (first write wins server-side).
export function attributeAccount() {
  const a = getAttribution();
  if (!Object.keys(a.first_touch).length && !Object.keys(a.last_touch).length) return;
  api.post("/analytics/attribute", a).catch(() => {});
}

// P9 — global error monitoring: message class + route only. No stack traces,
// no request payloads, no customer data ever leave this handler.
export function initErrorMonitor() {
  if (window.__bfErrMon) return;
  window.__bfErrMon = true;
  window.addEventListener("error", (e) => {
    if (!e.message) return;
    trackEvent("client_error", { detail: `js:${String(e.message).slice(0, 120)}`, once: false });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const msg = (e.reason && e.reason.message) || e.reason || "unknown";
    trackEvent("client_error", { detail: `promise:${String(msg).slice(0, 120)}`, once: false });
  });
}
